/** Bloc Entreprise — logo + onglets Détails / Adresse / Valeurs par défaut. */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { useCompany } from "../../company/CompanyProvider";
import { CompanyAddress } from "./CompanyAddress";
import { CompanyDefaults } from "./CompanyDefaults";
import { CompanyDetails } from "./CompanyDetails";
import { CompanyDelete } from "./CompanyDelete";
import { CompanyLogos } from "./CompanyLogos";
import { isCompanyOwner } from "./owner";
import { companyToDraft, type CompanyDraft } from "./types";

type CoTab = "details" | "address" | "defaults";

const TABS: { id: CoTab; label: string }[] = [
  { id: "details", label: "Détails" },
  { id: "address", label: "Adresse" },
  { id: "defaults", label: "Valeurs par défaut" },
];

export function CompanyTab() {
  const { company, updateCompany, reload } = useCompany();
  const [draft, setDraft] = useState<CompanyDraft | null>(null);
  const [tab, setTab] = useState<CoTab>("details");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;
    setDraft(companyToDraft(company));
  }, [company]);

  useEffect(() => {
    setMsg("");
    setErr("");
  }, [company?.id]);

  if (!company || !draft) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }

  const companyId = company.id;
  const current = draft;
  const owner = isCompanyOwner(company);

  async function save() {
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await updateCompany(companyId, current);
      setMsg("Enregistré.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-co-card">
      <header className="fx-co-card__head">
        <h2>Entreprise</h2>
        <p>Profil rond dans la barre · logo d’en-tête sur les devis et factures.</p>
        <p className="fx-co-form__hint">L’équipe se coche dans Pilotage, Apps du groupe.</p>
        {!owner ? (
          <p className="fx-co-form__hint">Lecture seule — seul le propriétaire enregistre la fiche.</p>
        ) : null}
      </header>
      <CompanyLogos
        company={company}
        owner={owner}
        busy={busy}
        setBusy={setBusy}
        onError={setErr}
        onOk={setMsg}
        onSaved={reload}
      />
      <div className="fx-co-tabs" role="tablist" aria-label="Entreprise">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? "is-on" : ""}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="fx-co-panel">
        {tab === "details" ? (
          <CompanyDetails
            companyId={companyId}
            draft={current}
            setDraft={setDraft}
            busy={busy}
            setBusy={setBusy}
            onError={setErr}
          />
        ) : null}
        {tab === "address" ? <CompanyAddress draft={current} setDraft={setDraft} /> : null}
        {tab === "defaults" ? <CompanyDefaults draft={current} setDraft={setDraft} /> : null}
      </div>
      {err ? <p className="fx-co-form__err">{err}</p> : null}
      {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
      <div className="fx-co-card__foot">
        <Button disabled={busy || !owner || !current.legal_name.trim()} onClick={() => void save()}>
          Enregistrer
        </Button>
      </div>
      {owner ? <CompanyDelete companyId={companyId} legalName={company.legal_name} /> : null}
    </div>
  );
}
